/**
 * WorkingTierSetupPanel
 *
 * Inline panel for assigning users and groups to working-tier org unit nodes:
 * the cycle scope organization (when applicable) and its direct divisions.
 * These assignments determine who receives the Catchball ball.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, UserPlus, X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { getWorkingTierAssignableOrgUnits } from '../../../lib/orgUnitTree';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import type { OrgUnit } from '../../../types/prioritization/enterpriseIdentity';
import type { Group } from '../../../stores/prioritization/groupsStore';

interface SystemUser {
  id: string;
  name?: string;
  email?: string;
}

interface UserPlacement {
  id: string;
  userId: string;
  name: string;
  email: string;
}

interface GroupPlacement {
  id: string;
  groupId: string;
  groupName: string;
}

interface NodeState {
  userPlacements: UserPlacement[];
  groupPlacements: GroupPlacement[];
  loading: boolean;
}

interface WorkingTierSetupPanelProps {
  orgScopeNodeId: string;
  orgUnitsMap: Map<string, OrgUnit>;
  systemUsers: SystemUser[];
  groups: Group[];
  onMembersChanged?: () => void;
}

export function WorkingTierSetupPanel({
  orgScopeNodeId,
  orgUnitsMap,
  systemUsers,
  groups,
  onMembersChanged,
}: WorkingTierSetupPanelProps) {
  const fetchEnterpriseIdentity = useEnterpriseIdentityStore(s => s.fetchFromSupabase);

  const tierNodes = useMemo(
    () => getWorkingTierAssignableOrgUnits(orgScopeNodeId, Array.from(orgUnitsMap.values())),
    [orgScopeNodeId, orgUnitsMap],
  );

  const [nodeStates, setNodeStates] = useState<Record<string, NodeState>>({});
  const [expandedNode, setExpandedNode] = useState<string | null>(
    tierNodes.length === 1 ? tierNodes[0]?.id ?? null : null
  );
  const [addingTo, setAddingTo] = useState<{ nodeId: string; type: 'user' | 'group' } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNode = useCallback(async (nodeId: string) => {
    setNodeStates(prev => ({ ...prev, [nodeId]: { ...prev[nodeId] ?? { userPlacements: [], groupPlacements: [] }, loading: true } }));
    try {
      const supabase = getSupabaseClient();
      const [{ data: userRows }, { data: groupRows }] = await Promise.all([
        supabase.from('user_node_assignments').select('id, user_id').eq('org_unit_id', nodeId).eq('active', true),
        supabase.from('group_node_assignments').select('id, group_id').eq('org_unit_id', nodeId),
      ]);

      type UserAssignmentRow = { id: string; user_id: string };
      type GroupAssignmentRow = { id: string; group_id: string };
      type UserRecordRow = { id: string; name: string | null; email: string | null };

      const userIds = (userRows ?? []).map((r: UserAssignmentRow) => r.user_id);
      const userNameMap = new Map<string, { name: string; email: string }>();
      if (userIds.length > 0) {
        const { data: usersData } = await supabase.from('users').select('id, name, email').in('id', userIds);
        (usersData ?? []).forEach((u: UserRecordRow) =>
          userNameMap.set(u.id, { name: u.name ?? '', email: u.email ?? '' }),
        );
      }

      const userPlacements: UserPlacement[] = (userRows ?? []).map((r: UserAssignmentRow) => ({
        id: r.id,
        userId: r.user_id,
        name: userNameMap.get(r.user_id)?.name ?? '',
        email: userNameMap.get(r.user_id)?.email ?? r.user_id,
      }));

      const groupPlacements: GroupPlacement[] = (groupRows ?? []).map((r: GroupAssignmentRow) => ({
        id: r.id,
        groupId: r.group_id,
        groupName: groups.find(g => g.id === r.group_id)?.displayName ?? r.group_id,
      }));

      setNodeStates(prev => ({ ...prev, [nodeId]: { userPlacements, groupPlacements, loading: false } }));
    } catch {
      setNodeStates(prev => ({ ...prev, [nodeId]: { userPlacements: [], groupPlacements: [], loading: false } }));
    }
  }, [groups]);

  useEffect(() => {
    tierNodes.forEach(node => loadNode(node.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgScopeNodeId, tierNodes.length]);

  const handleAddUser = async (nodeId: string) => {
    if (!selectedUserId) return;
    setSaving(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { error: insertError } = await supabase.from('user_node_assignments').insert({
        user_id: selectedUserId,
        org_unit_id: nodeId,
        inheritable: false,
        active: true,
        granted_by: 'admin',
        grant_source: 'manual',
      });
      if (insertError) throw insertError;
      setAddingTo(null);
      setSelectedUserId('');
      setUserSearch('');
      await loadNode(nodeId);
      void fetchEnterpriseIdentity();
      onMembersChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign user');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGroup = async (nodeId: string) => {
    if (!selectedGroupId) return;
    setSaving(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { error: insertError } = await supabase.from('group_node_assignments').insert({
        group_id: selectedGroupId,
        org_unit_id: nodeId,
        granted_by: 'admin',
      });
      if (insertError) throw insertError;
      setAddingTo(null);
      setSelectedGroupId('');
      await loadNode(nodeId);
      onMembersChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign group');
    } finally {
      setSaving(false);
    }
  };

  // Both of these used to await the delete without reading its result and then
  // swallow anything thrown — supabase-js RESOLVES with { error } rather than
  // throwing, so a refused removal did nothing, said nothing, and left the row
  // on screen. The panel already has an error line; these paths just never used it.
  const handleRemoveUser = async (nodeId: string, placementId: string) => {
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { error: removeError } = await supabase.from('user_node_assignments').delete().eq('id', placementId);
      if (removeError) throw removeError;
      await loadNode(nodeId);
      void fetchEnterpriseIdentity();
      onMembersChanged?.();
    } catch (err) {
      setError(`Could not remove this person: ${err instanceof Error ? err.message : 'the database refused the change'}`);
    }
  };

  const handleRemoveGroup = async (nodeId: string, placementId: string) => {
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { error: removeError } = await supabase.from('group_node_assignments').delete().eq('id', placementId);
      if (removeError) throw removeError;
      await loadNode(nodeId);
      onMembersChanged?.();
    } catch (err) {
      setError(`Could not remove this group: ${err instanceof Error ? err.message : 'the database refused the change'}`);
    }
  };

  const filteredUsers = systemUsers.filter(u => {
    const q = userSearch.toLowerCase();
    return (
      !q ||
      (u.name ?? '').toLowerCase().includes(q) ||
      (u.email ?? '').toLowerCase().includes(q)
    );
  });

  if (tierNodes.length === 0) {
    return (
      <div className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]/40 px-3 py-2">
        No org units found for the working tier under this cycle scope. Add teams or departments in{' '}
        <a href="/prioritization/settings/org-hierarchy" className="underline">Org Hierarchy settings</a> first.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="text-xs text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]/40 rounded px-3 py-1.5 flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-[var(--coral)] hover:text-[var(--coral)]">×</button>
        </div>
      )}

      {tierNodes.map(node => {
        const isScopeNode = node.id === orgScopeNodeId;
        const state = nodeStates[node.id] ?? { userPlacements: [], groupPlacements: [], loading: false };
        const totalMembers = state.userPlacements.length + state.groupPlacements.length;
        const isExpanded = expandedNode === node.id;
        const isAddingUser = addingTo?.nodeId === node.id && addingTo.type === 'user';
        const isAddingGroup = addingTo?.nodeId === node.id && addingTo.type === 'group';

        return (
          <div
            key={node.id}
            className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden"
          >
            {/* Node header — click to expand */}
            <button
              type="button"
              onClick={() => setExpandedNode(isExpanded ? null : node.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 flex items-center justify-center flex-shrink-0">
                <Users className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                    {node.displayName}
                  </p>
                  {isScopeNode && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-primary-100 text-primary-800 dark:bg-primary-950/40 dark:text-primary-300">
                      Organization
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] capitalize">
                  {node.type?.replace('-', ' ')}
                  {state.loading ? (
                    <span className="ml-1.5">· loading…</span>
                  ) : totalMembers === 0 ? (
                    <span className="ml-1.5 text-[var(--mustard)] dark:text-[var(--mustard)]">· no members assigned</span>
                  ) : (
                    <span className="ml-1.5 text-[var(--green)] dark:text-[var(--text-ter)]">
                      · {state.userPlacements.length} user{state.userPlacements.length !== 1 ? 's' : ''}
                      {state.groupPlacements.length > 0 && `, ${state.groupPlacements.length} group${state.groupPlacements.length !== 1 ? 's' : ''}`}
                    </span>
                  )}
                </p>
              </div>
              {state.loading
                ? <Loader2 className="h-3.5 w-3.5 text-[var(--text-ter)] animate-spin flex-shrink-0" />
                : isExpanded
                  ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                  : <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
              }
            </button>

            {/* Expanded body */}
            {isExpanded && (
              <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] px-4 pb-4 pt-3 space-y-3">

                {/* Current users */}
                {state.userPlacements.length > 0 && (
                  <div className="space-y-1">
                    {state.userPlacements.map(p => (
                      <div key={p.id} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 flex items-center justify-center flex-shrink-0 text-xs font-bold text-[var(--text-sec)]">
                          {(p.name || p.email).charAt(0).toUpperCase()}
                        </div>
                        <span className="flex-1 text-[var(--text-pri)] dark:text-[var(--text-sec)] truncate">
                          {p.name || p.email}
                          {p.name && p.email && (
                            <span className="ml-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">{p.email}</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => void handleRemoveUser(node.id, p.id)}
                          className="text-[var(--text-ter)] dark:text-[var(--text-dim)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors flex-shrink-0"
                          title="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Current groups */}
                {state.groupPlacements.length > 0 && (
                  <div className="space-y-1">
                    {state.groupPlacements.map(p => (
                      <div key={p.id} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 flex items-center justify-center flex-shrink-0">
                          <Users className="h-3 w-3 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                        </div>
                        <span className="flex-1 text-[var(--text-pri)] dark:text-[var(--text-sec)] truncate">{p.groupName}</span>
                        <button
                          type="button"
                          onClick={() => void handleRemoveGroup(node.id, p.id)}
                          className="text-[var(--text-ter)] dark:text-[var(--text-dim)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors flex-shrink-0"
                          title="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {totalMembers === 0 && !isAddingUser && !isAddingGroup && (
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] italic">
                    No members yet. Add users or groups below.
                  </p>
                )}

                {/* Add user form */}
                {isAddingUser && (
                  <div className="space-y-2 pt-1">
                    <input
                      type="text"
                      value={userSearch}
                      onChange={e => { setUserSearch(e.target.value); setSelectedUserId(''); }}
                      placeholder="Search by name or email…"
                      className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    {userSearch && (
                      <div className="max-h-40 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                        {filteredUsers.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-[var(--text-ter)]">No users found</p>
                        ) : filteredUsers.slice(0, 20).map(u => {
                          const alreadyAssigned = state.userPlacements.some(p => p.userId === u.id);
                          return (
                            <button
                              key={u.id}
                              type="button"
                              disabled={alreadyAssigned}
                              onClick={() => {
                                setSelectedUserId(u.id);
                                setUserSearch(u.name ?? u.email ?? '');
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                                selectedUserId === u.id
                                  ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)]'
                                  : alreadyAssigned
                                    ? 'opacity-40 cursor-not-allowed text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                                    : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-pri)] dark:text-[var(--text-sec)]'
                              }`}
                            >
                              <span className="font-medium truncate">{u.name ?? u.email}</span>
                              {u.name && u.email && <span className="text-xs text-[var(--text-ter)] truncate">{u.email}</span>}
                              {alreadyAssigned && <span className="ml-auto text-xs text-[var(--text-ter)]">assigned</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void handleAddUser(node.id)}
                        disabled={!selectedUserId || saving}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-40 transition-colors"
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
                        Add user
                      </button>
                      <button
                        type="button"
                        onClick={() => { setAddingTo(null); setSelectedUserId(''); setUserSearch(''); }}
                        className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Add group form */}
                {isAddingGroup && (
                  <div className="space-y-2 pt-1">
                    <select
                      value={selectedGroupId}
                      onChange={e => setSelectedGroupId(e.target.value)}
                      className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    >
                      <option value="">Choose a group…</option>
                      {groups
                        .filter(g => !state.groupPlacements.some(p => p.groupId === g.id))
                        .map(g => (
                          <option key={g.id} value={g.id}>{g.displayName}</option>
                        ))}
                    </select>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void handleAddGroup(node.id)}
                        disabled={!selectedGroupId || saving}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-40 transition-colors"
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users className="h-3 w-3" />}
                        Add group
                      </button>
                      <button
                        type="button"
                        onClick={() => { setAddingTo(null); setSelectedGroupId(''); }}
                        className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Add buttons (shown when not already in an add form) */}
                {!isAddingUser && !isAddingGroup && (
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => { setAddingTo({ nodeId: node.id, type: 'user' }); setExpandedNode(node.id); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 border border-[var(--slate)] dark:border-[var(--slate)]/50 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/50 transition-colors"
                    >
                      <UserPlus className="h-3 w-3" />
                      Add user
                    </button>
                    {groups.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setAddingTo({ nodeId: node.id, type: 'group' }); setExpandedNode(node.id); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 border border-[var(--slate)] dark:border-[var(--slate)]/50 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/50 transition-colors"
                      >
                        <Users className="h-3 w-3" />
                        Add group
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
